/**
 * Build the reporter that keeps `AFTER_MIDNIGHT_SECTION_SHOWN` to one event per search.
 *
 * The rule is about a lifetime, so the lifetime is the point: one reporter belongs to one mounted
 * results page. `ProviderDepartures` builds it in its constructor, which gives it exactly the span
 * that is wanted — it survives the `hasResults` ternary that unmounts and remounts the section
 * (a guard owned by the section would be reborn and report twice), and it dies with the page
 * (a module-level guard would outlive it, and `Switch` renders `Providers` INSTEAD of the results
 * route, so bouncing to provider selection and back is a real second impression that a surviving
 * guard would swallow).
 *
 * Keyed by `searchId` alone — not by the section's date, which can change between polls of one
 * search, and not by anything scoped to the active provider, since switching provider tabs keeps
 * the page mounted and is not a new impression.
 *
 * It lives here rather than inline in the component because reaching it through the component means
 * importing the whole results page; the rule is worth exercising on its own.
 *
 * @param {Function} track - The tracker to call, given the payload.
 * @returns {Function} `(searchId, payload)`, which reports at most once per `searchId` seen.
 */
export default function createAfterMidnightImpressionReporter(track) {
  const reported = new Set();

  return (searchId, payload) => {
    if (reported.has(searchId)) return;

    // Every search seen by this page, not just the last one. Remembering only the last made
    // `search-1 → search-2 → search-1` report three times: the carousel keeps this page mounted
    // while the search changes, and coming back to a date already seen is the same impression.
    //
    // Not capped. A cap is what would break the contract: evicting the oldest id means returning to
    // that search reports twice. The set is already bounded by the page — it dies with the results
    // listing — and its entries are search ids, so the ceiling is how many dates one visit browses.
    reported.add(searchId);

    track(payload);
  };
}
